<template>
    <a
        v-if="link || href"
        :class="classes"
        :style="{ ...styles, ...iconBackground}"
        :href="href"
        :title="title"
        :target="target"
        :disabled="disabled"
    >
        <span v-if="getIcon && !iconBackground">
            <i :class="{ [getIcon]: true, [getIconSize]: true }"></i>
        </span>
        <span><slot></slot></span>
    </a>
    <button
        v-else
        :class="classes"
        :style="{ ...styles, ...iconBackground}"
        :title="title"
        :disabled="disabled"
        @click="$emit('click', $event)"
    >
        <span class="base-button--icon" v-if="getIcon && !iconBackground">
            <i :class="{ [getIcon]: true, [getIconSize]: true }"></i>
        </span>
        <span v-if="!slotEmpty" class="base-button--text"><slot></slot></span>
    </button>
</template>

<script>
export default {
  name: 'BaseButton',
  data() {
    return {
      iconBackground: null,
      slotEmpty: true,
    };
  },
  props: {
    outlined: {
      type: Boolean,
      default: false,
    },
    rounded: {
      type: Boolean,
      default: false,
    },
    text: {
      type: Boolean,
      default: false,
    },
    link: {
      type: Boolean,
      default: false,
    },
    target: {
      type: String,
      default: null,
    },
    href: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    shadow: {
      type: Boolean,
      default: false,
    },
    highlight: {
      type: Boolean,
      default: false,
    },
    fill: {
      type: Boolean,
      default: false,
    },
    left: {
      type: Boolean,
      default: false,
    },
    inline: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'button',
    },
    download: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      validator(value) {
        return ['', 's', 'xs'].indexOf(value) !== -1;
      },
      default: '',
    },
    icon: {
      type: String,
      default: '',
    },
    iconSize: {
      type: Number,
      validator(value) {
        return value < 0 ? false : !(value >= 9);
      },
      default: 0,
    },
    backgroundColor: {
      type: String,
      default: 'transparent',
    },
    pressed: {
      type: Boolean,
      default: false,
    },
    autoHeight: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    this.slotEmpty = !this.$slots.default;
  },
  computed: {
    getIconSize() {
      switch (this.iconSize) {
        case 1:
          return 'fa-xs';
        case 2:
          return 'fa-sm';
        case 3:
          return 'fa-lg';
        case 4:
          return 'fa-2x';
        case 5:
          return 'fa-3x';
        case 6:
          return 'fa-5x';
        case 7:
          return 'fa-7x';
        case 8:
          return 'fa-10x';
        default:
          return this.slotEmpty ? 'fa-sm' : 'fa-lg';
      }
    },
    getIcon() {
      if (this.icon.includes('custom:')) {
        this.iconBackground = {
          backgroundImage: `url("img/icons/${this.icon.replace('custom:', '')}.svg")`,
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          height: 'var(--base-button-height)',
        };
      }
      return this.icon;
    },
    styles() {
      return {
        '--base-button-text-alignment': this.left ? 'flex-start' : 'center',
        '--base-button-height': this.autoHeight
          ? 'auto'
          : this.size === 's'
            ? '3.5rem'
            : this.size === 'xs'
              ? '3rem'
              : '65px',
        '--base-button-background-color': this.backgroundColor,
      };
    },
    classes() {
      return {
        'base-button': true,
        [`base-button--${this.size}`]: this.size,
        icon: this.icon,
        text: this.text,
        outlined: this.outlined,
        rounded: this.rounded,
        highlight: this.highlight,
        pressed: this.pressed,
        shadow: this.shadow,
        fill: this.fill,
        download: this.download,
        disabled: this.disabled,
        slotEmpty: this.slotEmpty,
        'base-button--inline': this.inline,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.base-button {
    @apply relative flex items-center;
    @apply cursor-pointer whitespace-nowrap text-2xl pt-4 pb-4 pl-8 pr-8;

    justify-content: var(--base-button-text-alignment);
    background-color: var(--base-button-background-color);
    transition: 125ms all ease-in-out;
    height: var(--base-button-height);
    &.fill {
        flex: 1 0 0;
    }

    &.icon:not(.slotEmpty) .base-button--icon {
        padding: 0 1rem 0 0;
    }

    &.icon:not(.slotEmpty) .base-button--text {
        padding: 0;
    }

    &--text {
        display: flex;
        align-items: center;
        justify-content: var(--base-button-text-alignment);
        width: 100%;
    }

    &a {
        transition: 250ms all ease-in-out;
    }

    &svg,
    &a:hover {
        transition: 250ms all ease-in-out;
        color: currentColor;
        text-decoration: none;
    }

    &.icon.slotEmpty {
        @apply p-0;

        width: 65px;
        font-size: 2rem;
    }

    &.highlight {
        margin: 2px;
    }

    &.outlined {
        margin: 2px;
        box-shadow: 0 0 0 1px currentColor !important;
    }

    &.rounded {
        border-radius: 9999px !important;
    }

    &.shadow {
        box-shadow: 0 0 0 0 currentColor !important;

        &.outlined {
            box-shadow: 0 0 0 1px currentColor !important;
        }

        &:hover {
            box-shadow: 0 0 5px 1px currentColor !important;
        }

        &.highlight:hover {
            box-shadow: 0 0 5px 1px var(--company-main-primary) !important;
        }
    }

    &--inline {
        padding: 0 !important;
        grid-column-gap: 0.5em;
        width: min-content;
    }

    &--s {
        @apply text-lg pt-3 pb-3 pl-7 pr-7;

        height: 3.5rem;

        &.icon.slotEmpty {
            width: 3.5rem;
        }
    }

    &--xs {
        @apply text-lg pt-3 pb-3 pl-5 pr-5;

        height: 3rem;

        &.icon.slotEmpty {
            width: 3rem;
        }
    }
}

.base-button:not(.text){
    color: var(--company-main-primary);
    text-decoration: none;

    &.outlined {
        box-shadow: 0 0 0 1px var(--company-main-primary);
    }

    &.highlight:hover,
    &.highlight.pressed {
        background-color: var(--company-main-primary);
        color: #fff;

        &svg,
        &a {
            color: #fff;
        }
    }

    &.disabled {
        color: rgb(195 195 195);

        &.highlight:hover,
        &.highlight.pressed {
            background-color: var(--base-button-background-color);
            color: rgb(195 195 195);

            &svg,
            &a {
                color: rgb(195 195 195);
            }
        }
    }
}

.base-button.text {
    color: currentColor;

    &.highlight:hover {
        &.outlined {
            box-shadow: 0 0 0 1px var(--company-main-primary);
        }

        color: var(--company-main-primary);

        &svg {
            color: var(--company-main-primary);
        }
    }
}

button:disabled,
button[disabled]{
    background-color: transparent !important;
    color: var(--company-main-primary) !important;
}

</style>
